import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingSkeleton, EmptyState } from "@/components/Layout";
import { SeverityBadge, StatusPill } from "@/components/Badges";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate } from "@/lib/dates";
import { AlertTriangle, Plus } from "lucide-react";

export default function Findings() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [f, r] = await Promise.all([api.get("/findings"), api.get("/risks")]);
      setItems(f.data); setRisks(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        kicker="Assessments"
        title="Findings"
        description="Manual findings from audits, pentests, and reviews. Linked to risks."
        actions={can("finding.write") && <Button className="bg-[#0A1329] hover:bg-[#162347] text-white" onClick={() => setOpen(true)} data-testid="btn-new-finding"><Plus className="h-4 w-4 mr-1.5" /> New Finding</Button>}
      />
      {loading ? <LoadingSkeleton /> : items.length === 0 ? (
        <EmptyState icon={AlertTriangle} title="No findings" description="Record findings from your latest audit or pentest." />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm" data-testid="findings-table">
            <thead className="bg-slate-50 border-b"><tr className="text-left">
              {["Title", "Severity", "Status", "Risk", "Due"].map((h) => <th key={h} className="px-4 py-2.5 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((f) => {
                const r = risks.find((r) => r.id === f.risk_id);
                return (
                  <tr key={f.id} onClick={() => can("finding.write") && (setEditing(f), setOpen(true))} className="border-b border-slate-100 hover:bg-cyan-50/30 cursor-pointer" data-testid={`row-finding-${f.id.slice(0, 8)}`}>
                    <td className="px-4 py-3 text-slate-900">{f.title}<div className="text-xs text-slate-500 mt-0.5">{f.description}</div></td>
                    <td className="px-4 py-3"><SeverityBadge severity={f.severity} /></td>
                    <td className="px-4 py-3"><StatusPill status={f.status} /></td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{r?.code || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 font-mono">{f.due_date ? formatDate(f.due_date) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
      {open && <FindingDialog risks={risks} existing={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={() => { setOpen(false); setEditing(null); load(); }} />}
    </div>
  );
}

function FindingDialog({ risks, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(existing ? { ...existing, due_date: existing.due_date ? existing.due_date.slice(0, 10) : "" } : { title: "", description: "", severity: "Moderate", status: "Open", risk_id: "", due_date: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = { ...form, risk_id: form.risk_id || null, due_date: form.due_date ? new Date(form.due_date).toISOString() : null };
      if (isEdit) await api.patch(`/findings/${existing.id}`, payload);
      else await api.post("/findings", payload);
      toast.success(isEdit ? "Finding updated" : "Finding created"); onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!isEdit || !window.confirm("Delete finding?")) return;
    try { await api.delete(`/findings/${existing.id}`); toast.success("Deleted"); onSaved?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="finding-dialog">
        <DialogHeader><DialogTitle className="font-display">New finding</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="find-title" /></div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="find-sev"><SelectValue /></SelectTrigger>
                <SelectContent>{["Low", "Moderate", "High", "Critical"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Open", "InProgress", "Closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Linked risk</Label>
            <Select value={form.risk_id || "none"} onValueChange={(v) => setForm({ ...form, risk_id: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="Unlinked" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unlinked</SelectItem>
                {risks.map((r) => <SelectItem key={r.id} value={r.id}>{r.code} — {r.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
        </div>
        <DialogFooter>
          {isEdit && <Button variant="ghost" className="mr-auto text-rose-700" onClick={del}>Delete</Button>}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="find-submit">{isEdit ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
